import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  isSuggestionGenerationApplicable,
  useGenerateSuggestionCommitMessage,
  type SuggestionCommitMessageGenerationTerminalState,
  type SuggestionGenerationConfig,
} from './use-generate-suggestion-commit-message'

type CommitMessageDraft = {
  message: string
  suggestedMessage?: string
  edited: boolean
  description: string
  descriptionEdited: boolean
  submitted: boolean
  batchKey?: string
  announcement?: string
  announcementTarget?: 'description'
  announcementVersion: number
}

export function useSuggestionCommitMessage({
  commentKind,
  generationConfig,
  fallbackCommitMessage,
  isActive = true,
  isSubmitting = false,
}: {
  commentKind: 'ccr' | 'human' | 'mixed'
  generationConfig?: SuggestionGenerationConfig
  fallbackCommitMessage: string
  isActive?: boolean
  isSubmitting?: boolean
}) {
  const {sendAnalyticsEvent} = useAnalytics()
  const inputRef = useRef<HTMLInputElement>(null)
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null)
  const applicable = isSuggestionGenerationApplicable({commentKind, generationConfig})
  const batchKey = generationConfig?.batchKey
  const [draft, setDraft] = useState<CommitMessageDraft>({
    message: applicable ? '' : fallbackCommitMessage,
    edited: false,
    description: '',
    descriptionEdited: false,
    submitted: false,
    batchKey,
    announcementVersion: 0,
  })

  const onGenerationSettled = useCallback(
    (settled: SuggestionCommitMessageGenerationTerminalState) => {
      const eventNames = {
        success: 'comments.commit_message_generation_succeeded',
        'no-title': 'comments.commit_message_generation_fallback',
        error: 'comments.commit_message_generation_errored',
      }
      sendAnalyticsEvent(eventNames[settled.status], 'CCR_COMMIT_MESSAGE_GENERATION', {
        ...(generationConfig?.batchKey
          ? {batchSize: generationConfig.batchSize}
          : {automatedCommentId: generationConfig?.automatedCommentId}),
        durationMs: settled.durationMs,
      })
    },
    [generationConfig, sendAnalyticsEvent],
  )
  const {generatedCommitMessage, generationState} = useGenerateSuggestionCommitMessage({
    commentKind,
    generationConfig,
    isActive,
    onGenerationSettled,
  })

  // Synchronize before rendering so a changed batch never submits the old generated text.
  // Keep manual edits, and freeze both submitted fields until the attempt settles.
  let nextDraft = draft
  if (draft.batchKey !== batchKey && !isSubmitting) {
    nextDraft = {
      ...draft,
      batchKey,
      submitted: false,
      ...(!draft.edited ? {message: applicable ? '' : fallbackCommitMessage, suggestedMessage: undefined} : {}),
      ...(!draft.descriptionEdited ? {description: ''} : {}),
    }
  }
  if (isActive && !isSubmitting && !nextDraft.submitted) {
    const shouldUseFallback = !applicable || generationState.status === 'error' || generationState.status === 'no-title'
    if (generationState.status === 'success' && generatedCommitMessage) {
      if (!nextDraft.edited && nextDraft.message !== generatedCommitMessage.commitMessage) {
        nextDraft = {
          ...nextDraft,
          message: generatedCommitMessage.commitMessage,
          suggestedMessage: generatedCommitMessage.commitMessage,
        }
      }
      const description = generatedCommitMessage.description ?? ''
      if (!nextDraft.descriptionEdited && nextDraft.description !== description) {
        nextDraft = {...nextDraft, description}
      }
    } else if (shouldUseFallback) {
      if (
        !nextDraft.edited &&
        (nextDraft.message !== fallbackCommitMessage || nextDraft.suggestedMessage !== undefined)
      ) {
        nextDraft = {...nextDraft, message: fallbackCommitMessage, suggestedMessage: undefined}
      }
      if (!nextDraft.descriptionEdited && nextDraft.description !== '') {
        nextDraft = {...nextDraft, description: ''}
      }
    }
    // Compare the rendered values, including batch resets, rather than intermediate draft writes.
    const announcements: string[] = []
    const titleUpdated = nextDraft.message !== draft.message
    if (titleUpdated) {
      announcements.push(
        nextDraft.suggestedMessage !== undefined
          ? 'Commit message updated with a generated suggestion.'
          : nextDraft.message === ''
            ? 'Commit message cleared for the updated batch.'
            : "Copilot couldn't generate a commit message. Using the default commit message instead.",
      )
    }
    if (nextDraft.description !== draft.description) {
      announcements.push(
        nextDraft.description
          ? 'Commit description updated with a generated suggestion.'
          : 'Generated commit description cleared.',
      )
    }
    if (announcements.length > 0) {
      nextDraft = {
        ...nextDraft,
        announcement: announcements.join(' '),
        announcementTarget: titleUpdated ? undefined : 'description',
        announcementVersion: draft.announcementVersion + 1,
      }
    }
  }
  if (nextDraft !== draft) setDraft(nextDraft)

  const announcedVersion = useRef(0)
  useEffect(() => {
    if (!isActive || !draft.announcement || announcedVersion.current === draft.announcementVersion) return
    announcedVersion.current = draft.announcementVersion
    const announcementRef = draft.announcementTarget === 'description' ? descriptionInputRef : inputRef
    announce(draft.announcement, {element: announcementRef.current ?? undefined})
  }, [draft.announcement, draft.announcementTarget, draft.announcementVersion, isActive])

  const setCommitMessage = useCallback((message: string) => {
    setDraft(current => ({...current, message, edited: true, suggestedMessage: undefined}))
  }, [])
  const setExtendedDescription = useCallback((description: string) => {
    setDraft(current => ({...current, description, descriptionEdited: true}))
  }, [])
  const markSubmitted = useCallback(() => setDraft(current => ({...current, submitted: true})), [])
  const isGenerating =
    applicable && !draft.submitted && (generationState.status === 'idle' || generationState.status === 'loading')
  const isPending = isGenerating && !draft.edited && !draft.message.trim()
  const isFallback =
    applicable &&
    !draft.edited &&
    draft.message === fallbackCommitMessage &&
    (generationState.status === 'error' || generationState.status === 'no-title')
  const caption = isPending
    ? 'Copilot is generating a commit message. Enter your own message or wait for Copilot to finish.'
    : isFallback
      ? "Copilot couldn't generate a commit message. Using the default commit message instead."
      : draft.suggestedMessage !== undefined
        ? 'Message suggested by Copilot.'
        : undefined

  return {
    commitMessage: draft.message,
    setCommitMessage,
    extendedDescription: draft.description,
    setExtendedDescription,
    markSubmitted,
    inputRef,
    descriptionInputRef,
    caption,
    descriptionCaption:
      isGenerating && !draft.descriptionEdited
        ? 'Copilot is generating a description. Enter your own description or wait for Copilot to finish.'
        : !draft.descriptionEdited && draft.description
          ? 'Description suggested by Copilot.'
          : undefined,
    placeholder: isPending ? 'Copilot is thinking...' : undefined,
  }
}
